import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { searchApi, officesApi, sarbRatesApi, api, notificationsApi } from '../lib/api';
import {
  LayoutDashboard, Users, Wallet, PiggyBank, FileText, LogOut,
  Building2, Scale, BookOpen, FileBarChart, History, ChevronDown, ChevronRight,
  Menu, X, Bell, Search, UserCircle, GitBranch, Settings, Shield,
  CreditCard, Receipt, Package, CalendarDays, Clock, Coins, Globe,
  User, UsersRound, MapPin, CheckSquare, Layers, ShieldAlert
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

const DashboardLayout = () => {
  const { user, logout, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [rates, setRates] = useState({ repo_rate: { rate: 8.25 }, zaronia: { rate: 8.10 } });
  const searchRef = useRef(null);
  const [viewAsRole, setViewAsRoleState] = useState(() => sessionStorage.getItem('viewAsRole') || null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);

  const setViewAsRole = (r) => {
    setViewAsRoleState(r);
    if (r) {
      sessionStorage.setItem('viewAsRole', r);
    } else {
      sessionStorage.removeItem('viewAsRole');
    }
  };

  const actualRole = user?.role || 'teller';
  const role = viewAsRole || actualRole;

  // Role-based access map
  const canSee = (section) => {
    const access = {
      admin:              { dashboard: true, clients: true, accounting: true, admin: true, reports: true, loans: true, savings: true, compliance: true },
      manager:            { dashboard: true, clients: true, accounting: true, admin: true, reports: true, loans: true, savings: true, compliance: true },
      loan_officer:       { dashboard: false, clients: true, accounting: false, admin: false, reports: false, loans: true, savings: true, compliance: false },
      accountant:         { dashboard: true, clients: false, accounting: true, admin: false, reports: true, loans: false, savings: false, compliance: false },
      teller:             { dashboard: false, clients: true, accounting: false, admin: false, reports: false, loans: false, savings: true, compliance: false },
      auditor:            { dashboard: true, clients: true, accounting: true, admin: true, reports: true, loans: true, savings: true, compliance: true },
      compliance_officer: { dashboard: true, clients: true, accounting: false, admin: false, reports: true, loans: true, savings: true, compliance: true },
    };
    return access[role]?.[section] ?? false;
  };

  useEffect(() => {
    loadSidebarData();
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSidebarData = async () => {
    try {
      const [hierRes, ratesRes] = await Promise.all([
        officesApi.hierarchy(),
        sarbRatesApi.current()
      ]);
      const hierData = Array.isArray(hierRes.data) ? hierRes.data : [];
      setHierarchy(hierData);
      if (ratesRes.data) setRates(ratesRes.data);
      // Auto-expand root only on first load
      setExpandedNodes(prev => {
        if (Object.keys(prev).length === 0) {
          const exp = {};
          hierData.forEach(o => { exp[o.id] = true; });
          return exp;
        }
        return prev;
      });
    } catch (e) { console.error(e); }
  };

  const loadNotifications = async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        notificationsApi.list(),
        notificationsApi.unreadCount(),
      ]);
      setNotifications(listRes.data);
      setUnreadCount(countRes.data.count);
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) { /* ignore */ }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      await notificationsApi.markRead(notif.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    }
    // Navigate to approval queue for pending changes
    if (notif.type === 'pending_product_change' || notif.type === 'pending_spread_override') {
      navigate('/dashboard/admin/approval-queue');
    }
    setShowNotifications(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await searchApi.search(q);
      setSearchResults(res.data);
      setShowSearch(true);
    } catch (e) { console.error(e); }
  };

  const toggleNode = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isActive = (path) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  // Redirect when viewAsRole changes to a restricted role
  useEffect(() => {
    if (viewAsRole && (viewAsRole === 'teller' || viewAsRole === 'loan_officer')) {
      if (location.pathname === '/dashboard' || location.pathname === '/dashboard/') {
        navigate('/dashboard/clients', { replace: true });
      }
    }
  }, [viewAsRole, location.pathname, navigate]);

  const topMenuActive = (paths) => paths.some(p => location.pathname.startsWith(p));

  // CBS menu items
  const clientsMenuItems = [
    { path: '/dashboard/clients', label: 'Clients', icon: Users },
    { path: '/dashboard/clients/groups', label: 'Groups', icon: UsersRound },
    { path: '/dashboard/clients/centers', label: 'Centers', icon: MapPin },
    ...(role !== 'loan_officer' ? [{ path: '/dashboard/clients/shares', label: 'Share Accounts', icon: Layers }] : []),
  ];

  const accountingMenuItems = [
    { path: '/dashboard/accounting', label: 'Chart of Accounts', icon: BookOpen },
    { path: '/dashboard/accounting/journal', label: 'Journal Entries', icon: FileText },
  ];

  const reportsMenuItems = [
    { path: '/dashboard/reports', label: 'Regulatory Reports', icon: FileBarChart },
    { path: '/dashboard/rates', label: 'Interest Rates', icon: FileText },
    { path: '/dashboard/audit', label: 'Audit Trail', icon: History },
  ];

  // Render office tree node for sidebar Navigation Tool
  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    const typeIcons = { head_office: Building2, regional_office: Globe, branch: GitBranch, sub_branch: MapPin };
    const Icon = typeIcons[node.office_type] || Building2;

    return (
      <div key={node.id}>
        <div
          className={`w-full flex items-center gap-2 py-1.5 px-2 text-left text-xs hover:bg-gray-100 transition-colors cursor-pointer`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          data-testid={`nav-office-${node.id}`}
        >
          {hasChildren ? (
            <button onClick={(e) => { e.stopPropagation(); toggleNode(node.id); }} className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded" data-testid={`toggle-office-${node.id}`}>
              {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
            </button>
          ) : <span className="w-3 flex-shrink-0" />}
          <button
            onClick={() => navigate(`/dashboard/clients?office_id=${node.id}&office_name=${encodeURIComponent(node.name)}`)}
            className="flex items-center gap-2 flex-1 min-w-0"
          >
            <Icon className="w-3.5 h-3.5 text-[#C5A059] flex-shrink-0" />
            <span className="text-gray-700 truncate">{node.name}</span>
          </button>
          {node.clients_count > 0 && <span className="ml-auto text-[10px] text-gray-400">{node.clients_count}</span>}
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map(child => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard-layout">
      {/* Top Header Bar - Mifos X style */}
      <header className="bg-[#1f1f1f] h-11 flex items-center justify-between px-4 fixed top-0 left-0 right-0 z-50" data-testid="dashboard-header">
        <div className="flex items-center gap-3">
          <button className="lg:hidden text-white" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/dashboard" className="flex items-center" data-testid="header-logo">
            <span className="text-white" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '9px', letterSpacing: '0.25em' }}>
              N K O S I ' S
            </span>
          </Link>

          {/* Top Menu Dropdowns */}
          <nav className="hidden lg:flex items-center ml-4">
            {/* Dashboard */}
            {canSee('dashboard') && (
            <Link
              to="/dashboard"
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                location.pathname === '/dashboard' ? 'bg-[#333333] text-white' : 'text-gray-300 hover:text-white hover:bg-[#333333]/50'
              }`}
              data-testid="nav-top-dashboard"
            >
              <LayoutDashboard className="w-3.5 h-3.5 inline mr-1.5" />Dashboard
            </Link>
            )}

            {/* Clients Dropdown */}
            {canSee('clients') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  topMenuActive(['/dashboard/clients']) ? 'bg-[#333333] text-white' : 'text-gray-300 hover:text-white hover:bg-[#333333]/50'
                }`} data-testid="nav-top-clients">
                  <Users className="w-3.5 h-3.5" />Clients<ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="start">
                {clientsMenuItems.map(item => (
                  <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)} data-testid={`menu-${item.label.toLowerCase()}`}>
                    <item.icon className="w-4 h-4 mr-2 text-gray-500" />{item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Accounting - goes to hub */}
            {canSee('accounting') && (
            <Link
              to="/dashboard/accounting"
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                topMenuActive(['/dashboard/accounting']) ? 'bg-[#333333] text-white' : 'text-gray-300 hover:text-white hover:bg-[#333333]/50'
              }`}
              data-testid="nav-top-accounting"
            >
              <Scale className="w-3.5 h-3.5 inline mr-1.5" />Accounting
            </Link>
            )}

            {/* Admin Dropdown - goes to hub pages */}
            {canSee('admin') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  topMenuActive(['/dashboard/admin', '/dashboard/settings']) ? 'bg-[#333333] text-white' : 'text-gray-300 hover:text-white hover:bg-[#333333]/50'
                }`} data-testid="nav-top-admin">
                  <Settings className="w-3.5 h-3.5" />Admin<ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuItem onClick={() => navigate('/dashboard/admin/users')} data-testid="menu-users">
                  <User className="w-4 h-4 mr-2 text-gray-500" />Users
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/admin/organization')} data-testid="menu-organization">
                  <Building2 className="w-4 h-4 mr-2 text-gray-500" />Organization
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/admin/products')} data-testid="menu-products">
                  <Package className="w-4 h-4 mr-2 text-gray-500" />Products
                </DropdownMenuItem>
                {role === 'admin' && (
                <DropdownMenuItem onClick={() => navigate('/dashboard/admin/system')} data-testid="menu-system">
                  <Settings className="w-4 h-4 mr-2 text-gray-500" />System
                </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/admin/approval-queue')} data-testid="menu-approval-queue">
                  <CheckSquare className="w-4 h-4 mr-2 text-gray-500" />Approval Queue
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Reports Dropdown */}
            {canSee('reports') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                  topMenuActive(['/dashboard/reports', '/dashboard/rates', '/dashboard/audit', '/dashboard/executive']) ? 'bg-[#333333] text-white' : 'text-gray-300 hover:text-white hover:bg-[#333333]/50'
                }`} data-testid="nav-top-reports">
                  <FileBarChart className="w-3.5 h-3.5" />Reports<ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48" align="start">
                {reportsMenuItems.map(item => (
                  <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)} data-testid={`menu-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                    <item.icon className="w-4 h-4 mr-2 text-gray-500" />{item.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/financial-reports')} data-testid="menu-financial-reports">
                  <FileBarChart className="w-4 h-4 mr-2 text-[#C5A059]" />Financial Reports
                </DropdownMenuItem>
                {(role === 'admin' || role === 'manager') && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard/executive')} data-testid="menu-executive-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2 text-[#C5A059]" />Executive Dashboard
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </nav>
        </div>

        {/* Right side: Search + Notifications + User */}
        <div className="flex items-center gap-3">
          {/* Global Search */}
          <div className="relative hidden md:block" ref={searchRef}>
            <div className="flex items-center bg-[#333333] rounded px-2 py-1">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSearch(true)}
                onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                placeholder="Search clients, loans..."
                className="bg-transparent text-xs text-white placeholder-gray-400 outline-none ml-2 w-40 border-none shadow-none ring-0 focus:ring-0 focus:outline-none"
                style={{ background: 'transparent', WebkitBoxShadow: 'none', boxShadow: 'none' }}
                autoComplete="off"
                data-testid="global-search-input"
              />
            </div>
            {showSearch && searchResults.length > 0 && (
              <div className="absolute top-full right-0 mt-1 w-72 bg-white border border-gray-200 shadow-lg z-50" data-testid="search-results">
                {searchResults.map((r, i) => (
                  <button key={i} onClick={() => { navigate(r.path); setShowSearch(false); setSearchQuery(''); }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 border-b last:border-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      r.type === 'client' ? 'bg-blue-50 text-blue-600' :
                      r.type === 'loan' ? 'bg-green-50 text-green-600' :
                      r.type === 'office' ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-600'
                    }`}>{r.type}</span>
                    <div className="min-w-0"><div className="text-xs font-medium text-gray-800 truncate">{r.title}</div><div className="text-[10px] text-gray-400">{r.subtitle}</div></div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={notifRef}>
            <button className="relative text-white/70 hover:text-white" data-testid="notifications-button" onClick={() => setShowNotifications(!showNotifications)}>
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1" data-testid="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 shadow-lg z-50 max-h-[400px] overflow-y-auto" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-xs font-semibold text-[#1f1f1f]">Notifications</span>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} className="text-[10px] text-[#C5A059] hover:underline" data-testid="mark-all-read-btn">Mark all read</button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-gray-400">No notifications</div>
                ) : (
                  notifications.slice(0, 20).map(n => (
                    <button
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.is_read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs leading-snug ${!n.is_read ? 'font-medium text-[#1f1f1f]' : 'text-gray-600'}`}>{n.message}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-1.5 text-white hover:bg-white/10 h-7 px-2" data-testid="user-menu-trigger">
                {user?.profile_picture_url ? (
                  <img src={user.profile_picture_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                ) : (
                  <UserCircle className="w-4 h-4" />
                )}
                <span className="hidden md:block text-xs">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.first_name || user?.last_name || 'User Name'}
                </span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs"><span className="font-medium">{user?.email}</span></DropdownMenuItem>
              <DropdownMenuItem className="text-xs text-gray-500">Role: {actualRole}{viewAsRole && ` (viewing as ${viewAsRole})`}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {actualRole === 'admin' && (
                <>
                  <DropdownMenuItem className="text-[10px] text-gray-400 cursor-default hover:bg-transparent" disabled>
                    View As Role
                  </DropdownMenuItem>
                  {['teller', 'loan_officer', 'accountant', 'manager', 'auditor'].map(r => (
                    <DropdownMenuItem key={r} onClick={() => setViewAsRole(viewAsRole === r ? null : r)} className={`text-xs ${viewAsRole === r ? 'bg-[#C5A059]/10 text-[#C5A059] font-medium' : ''}`} data-testid={`view-as-${r}`}>
                      {viewAsRole === r ? 'Exit ' : ''}{r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} View
                    </DropdownMenuItem>
                  ))}
                  {viewAsRole && (
                    <DropdownMenuItem onClick={() => setViewAsRole(null)} className="text-xs text-red-500 font-medium" data-testid="exit-view-as">
                      Back to Admin View
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { try { const res = await api.get('/admin/system-manual', { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `SystemManual_${new Date().toISOString().slice(0,10)}.pdf`; a.click(); } catch(e) { console.error(e); }}} className="text-xs" data-testid="download-manual">
                    <FileText className="w-3.5 h-3.5 mr-2" />Download System Manual
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => navigate('/dashboard/user-settings')} className="text-xs" data-testid="user-settings-link">
                <Settings className="w-3.5 h-3.5 mr-2" />User Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 text-xs" data-testid="logout-button">
                <LogOut className="w-3.5 h-3.5 mr-2" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* View-As Banner */}
      {viewAsRole && actualRole === 'admin' && (
        <div className="fixed top-11 left-0 right-0 z-50 bg-[#C5A059] text-[#1f1f1f] text-center py-1 text-xs font-medium" data-testid="view-as-banner">
          Viewing as: {viewAsRole.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          <button onClick={() => setViewAsRole(null)} className="ml-3 px-2 py-0.5 bg-[#1f1f1f] text-white text-[10px] rounded hover:bg-[#1f1f1f]/80">Exit</button>
        </div>
      )}

      {/* Left Sidebar - Navigation Tool */}
      <aside className={`fixed left-0 top-11 bottom-0 w-56 bg-white border-r border-gray-200 z-40 transition-transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-0'
      } overflow-hidden`} data-testid="dashboard-sidebar">
        <div className="h-full flex flex-col">
          {/* Navigation Tool Header */}
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#C5A059]">Navigation</p>
          </div>

          {/* Quick Links */}
          <div className="px-2 py-1 border-b border-gray-100">
            {[
              { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', section: 'dashboard' },
              { path: '/dashboard/clients', icon: Users, label: 'Clients', section: 'clients' },
              { path: '/dashboard/loans', icon: Wallet, label: 'Loans', section: 'loans' },
              { path: '/dashboard/savings', icon: PiggyBank, label: 'Savings', section: 'savings' },
              { path: '/dashboard/accounting', icon: Scale, label: 'Accounting', section: 'accounting' },
              { path: '/dashboard/compliance', icon: ShieldAlert, label: 'Compliance', section: 'compliance' },
            ].filter(item => canSee(item.section)).map(item => (
              <Link key={item.path} to={item.path}
                className={`flex items-center gap-2 px-2 py-1.5 text-xs transition-colors ${
                  isActive(item.path) ? 'bg-[#1f1f1f]/5 text-[#1f1f1f] font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-3.5 h-3.5" />{item.label}
              </Link>
            ))}
          </div>

          {/* Office Hierarchy Tree */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-1">Office Hierarchy</p>
            </div>
            <div className="px-1">
              {hierarchy.map(node => renderTreeNode(node))}
            </div>
          </div>

          {/* Rate Info Footer */}
          <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 mb-1">Current Rates</p>
            <div className="space-y-0.5">
              <div className="flex justify-between text-xs"><span className="text-gray-500">Repo</span><span className="font-medium text-[#1f1f1f]">{rates.repo_rate?.rate || 0}%</span></div>
              <div className="flex justify-between text-xs"><span className="text-gray-500">ZARONIA</span><span className="font-medium text-[#1f1f1f]">{rates.zaronia?.rate || 0}%</span></div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-11 transition-all ${sidebarOpen ? 'lg:ml-56' : ''}`} data-testid="dashboard-main">
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Forced password-change modal — appears when admin reset the user's
          password and they are still on the temp one. Cannot be dismissed
          until changed. */}
      <ChangePasswordDialog
        open={!!user?.must_change_password}
        forced
        onSuccess={() => { if (checkAuth) checkAuth(); }}
        onClose={() => {}}
      />
    </div>
  );
};

export default DashboardLayout;
